import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Mail, ChevronLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { authService } from "@/services/auth.service";
import { getErrorMessage } from "@/services/api";

export default function ForgotPasswordPage() {
    const [email, setEmail] = useState("");
    const [sent, setSent] = useState(null); // {message, reset_token?}
    const [submitting, setSubmitting] = useState(false);
    const navigate = useNavigate();

    async function submit(e) {
        e.preventDefault();
        setSubmitting(true);
        try {
            const res = await authService.forgotPassword(email);
            setSent(res);
            toast.success("Se o e-mail existir, enviaremos as instruções.");
        } catch (err) {
            toast.error(getErrorMessage(err, "Falha ao processar solicitação."));
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <div className="grid min-h-screen place-items-center p-6">
            <Card className="w-full max-w-md rounded-2xl border shadow-sm">
                <CardContent className="space-y-5 p-6 sm:p-8">
                    <button
                        type="button"
                        onClick={() => navigate(-1)}
                        className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
                        data-testid="forgot-back"
                    >
                        <ChevronLeft className="h-3.5 w-3.5" /> Voltar
                    </button>
                    <div className="space-y-1">
                        <div className="text-2xl font-semibold tracking-tight">Recuperar senha</div>
                        <p className="text-sm text-muted-foreground">
                            Informe seu e-mail e enviaremos um link de redefinição.
                        </p>
                    </div>

                    {sent ? (
                        <div className="space-y-3">
                            <div
                                className="rounded-lg border border-success/30 bg-success/10 px-3 py-2 text-sm text-success"
                                data-testid="forgot-success"
                            >
                                {sent.message}
                            </div>
                            {sent.reset_token && (
                                <div className="rounded-lg border bg-muted/40 p-3 text-[11px] text-muted-foreground">
                                    <div className="font-medium text-foreground/80">Modo desenvolvimento</div>
                                    <div>Token: <code className="break-all">{sent.reset_token}</code></div>
                                    <Link
                                        to={`/reset-password?token=${encodeURIComponent(sent.reset_token)}`}
                                        className="mt-2 inline-block text-secondary underline"
                                        data-testid="forgot-open-reset"
                                    >
                                        Abrir página de redefinição
                                    </Link>
                                </div>
                            )}
                        </div>
                    ) : (
                        <form onSubmit={submit} className="space-y-4">
                            <div className="space-y-1.5">
                                <Label htmlFor="email">E-mail</Label>
                                <Input
                                    id="email"
                                    type="email"
                                    required
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    data-testid="forgot-email-input"
                                />
                            </div>
                            <Button
                                type="submit"
                                className="w-full bg-secondary text-secondary-foreground hover:opacity-90"
                                disabled={submitting}
                                data-testid="forgot-submit"
                            >
                                {submitting ? (
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                ) : (
                                    <Mail className="mr-2 h-4 w-4" />
                                )}
                                Enviar link
                            </Button>
                        </form>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}
